/**
 * Wide horizontal bar showing the current phase's task progress split
 * into stacked segments (done / in_progress / not_started / blocked /
 * skipped). Each segment uses the same SIGNAL_COLOR palette as the DAG
 * view's status dots so a quick glance correlates across pages.
 *
 * Renders a numeric overlay (`done / effectiveTotal (donePercent%)`)
 * so the dashboard doesn't need a separate "X 件中 Y 件完了" callout.
 */
import type { ExecutionStatus } from '@mct/shared';

import { SIGNAL_COLOR } from '../DagView/signal';
import { statusPillClass } from '../../lib/statusColors';
import { cn } from '../../lib/utils';
import type { PhaseProgressBreakdown } from '../../lib/dashboard';

export interface PhaseProgressBarProps {
  breakdown: PhaseProgressBreakdown;
  className?: string;
}

export function PhaseProgressBar({ breakdown, className }: PhaseProgressBarProps): JSX.Element {
  const total = Math.max(breakdown.total, 1);
  const segments: Array<{ key: ExecutionStatus; count: number; color: string; label: string }> = [
    { key: 'done', count: breakdown.done, color: SIGNAL_COLOR.done, label: '完了' },
    { key: 'in_progress', count: breakdown.inProgress, color: SIGNAL_COLOR.in_progress, label: '進行中' },
    { key: 'blocked', count: breakdown.blocked, color: SIGNAL_COLOR.blocked, label: 'ブロック' },
    { key: 'not_started', count: breakdown.notStarted, color: SIGNAL_COLOR.not_started, label: '未着手' },
    { key: 'skipped', count: breakdown.skipped, color: SIGNAL_COLOR.skipped, label: 'スキップ' },
  ];
  return (
    <div className={className} data-testid="phase-progress-bar">
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100">
        {segments.map((seg) =>
          seg.count > 0 ? (
            <div
              key={seg.key}
              className="h-full"
              style={{ width: `${(seg.count / total) * 100}%`, backgroundColor: seg.color }}
              title={`${seg.label}: ${seg.count}`}
              data-segment={seg.key}
            />
          ) : null,
        )}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
        {segments.map((seg) => (
          <span
            key={seg.key}
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium',
              statusPillClass(seg.key),
            )}
          >
            {seg.label} <span className="tabular-nums">{seg.count}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
